/**
 * Where a layout element SITS in the 3D scene — its box, in metres.
 *
 * The plan view could get away with footprints alone; a 3D handle needs a Y band
 * too, and that is the one thing the interior layout cannot know (see
 * `interiorRenderPlan`'s `BenchSeatHeight`). Every number in here is therefore
 * read back rather than chosen:
 *
 *  - X/Z from `benchRunFootprint` / `heaterFootprint` — the same helpers the BOM
 *    and the validator measure with.
 *  - the bench Y band from `benchVerticalExtentM`, which is what
 *    `heaterClearanceLayout` already uses to build its obstacle boxes, so a
 *    handle cannot sit at a height the compliance frame disagrees with.
 *  - the floor plane and the heater's Y band from `resolveHeaterClearanceLayout`
 *    — i.e. from the engine's own build context (`ctx.wallBase`), not from a
 *    literal. `interactionOverlays.ts` hardcodes `wallBaseY = 0.165`; copying
 *    that number here would have made it a mirrored constant with two owners.
 *
 * One consequence worth stating: because the handle box and the drag GHOST both
 * come out of {@link interiorElementBoxM}, the ghost cannot land somewhere the
 * handle would not have been. They agree by construction, not by test.
 */

import { resolveWallHeightM } from "../../domain/geometry/buildingDimensions";
import {
	type BenchRun,
	type BenchRunEdge,
	benchRunFootprint,
	benchSeatTopMm,
	type FootprintMm,
	heaterFootprint,
	type InteriorLayout,
	type InteriorRoom,
} from "../../domain/interior";
import { resolveHeaterClearanceLayout } from "../../engines/interiorLayoutResolver";
import { benchVerticalExtentM } from "../../engines/interiorRenderPlan";
import type { BuildInput } from "../../engines/types";
import type { InteriorDragTarget } from "./interiorDragTarget";
import {
	deriveInteriorEditModel,
	type InteriorEditModel,
} from "./interiorEditModel";
import {
	createBenchEdgeMeasurementTarget,
	createBenchMeasurementTarget,
	createHeaterMeasurementTarget,
	getWallOverlayOpacities,
	type MeasurementTarget,
} from "./measurementTargets";
import { benchRunHandlePointsMm } from "./PlanView/planScene";

/** Axis-aligned box in the model/rig frame, metres. */
export interface InteriorElementBox {
	centerM: [number, number, number];
	sizeM: [number, number, number];
}

/**
 * The two engine-owned heights the layout model has no opinion about.
 *
 * `wallBaseY` is the finished floor plane (`SquareBaseBuildContext.wallBase`);
 * `heaterMinY`/`heaterMaxY` are the aggregat's own band, including the 5 mm floor
 * gap the engines leave under it.
 */
export interface InteriorFrameM {
	wallBaseY: number;
	wallHeightM: number;
	heaterMinY: number;
	heaterMaxY: number;
}

/**
 * Read the frame off the clearance layout.
 *
 * `resolveHeaterClearanceLayout` is called rather than composed by hand because
 * it is the single owner of "measure the clearance against the layout the model
 * actually has" — re-composing `deriveHeaterClearanceLayout(input, authored)`
 * here would be a second copy of exactly the seam issue 131 closed.
 */
export function deriveInteriorFrame(input: BuildInput): InteriorFrameM | null {
	const clearance = resolveHeaterClearanceLayout(input);
	if (!clearance) return null;
	return {
		wallBaseY: clearance.roomInnerBox.minY,
		wallHeightM: resolveWallHeightM(input.wallHeightM),
		heaterMinY: clearance.heaterBox.minY,
		heaterMaxY: clearance.heaterBox.maxY,
	};
}

function boxFrom(
	footprint: FootprintMm,
	minY: number,
	maxY: number,
): InteriorElementBox {
	return {
		centerM: [
			(footprint.minXMm + footprint.maxXMm) / 2000,
			(minY + maxY) / 2,
			(footprint.minZMm + footprint.maxZMm) / 2000,
		],
		sizeM: [
			Math.max(0, footprint.maxXMm - footprint.minXMm) / 1000,
			Math.max(0, maxY - minY),
			Math.max(0, footprint.maxZMm - footprint.minZMm) / 1000,
		],
	};
}

function roomForRun(
	run: BenchRun,
	rooms: readonly InteriorRoom[],
): InteriorRoom {
	return rooms.find((entry) => entry.id === run.room) ?? rooms[0];
}

/** The box a bench run occupies, exactly as `addSlattedBench` draws it. */
export function benchRunBoxM(
	run: BenchRun,
	rooms: readonly InteriorRoom[],
	frame: InteriorFrameM,
): InteriorElementBox {
	const extent = benchVerticalExtentM(
		run,
		frame.wallBaseY,
		frame.wallHeightM,
		roomForRun(run, rooms),
	);
	return boxFrom(
		benchRunFootprint(run, roomForRun(run, rooms)),
		extent.minY,
		extent.maxY,
	);
}

/**
 * The box a drag target occupies in a given layout — the handle at rest, and the
 * ghost while a gesture is live.
 *
 * `bench-edge` deliberately returns the WHOLE run: what a resize drag previews is
 * the resized bench, not the handle that pulls it.
 */
export function interiorElementBoxM(
	target: InteriorDragTarget,
	layout: InteriorLayout,
	rooms: readonly InteriorRoom[],
	heaterSizeMm: { widthMm: number; depthMm: number },
	frame: InteriorFrameM,
): InteriorElementBox | null {
	if (target.kind === "heater") {
		const hotRoom = rooms.find((room) => room.id === "sauna") ?? rooms[0];
		return boxFrom(
			heaterFootprint(layout.heater, hotRoom, heaterSizeMm),
			frame.heaterMinY,
			frame.heaterMaxY,
		);
	}

	// Null only for a run reconciliation has dropped mid-gesture, which is a real
	// case: the ghost hides rather than drawing a zero-sized box.
	const run = layout.benchRuns.find((entry) => entry.id === target.runId);
	if (!run) return null;
	return benchRunBoxM(run, rooms, frame);
}

/**
 * Height of the cube that pulls a run's end, and how far it stands proud of the
 * seat.
 *
 * It protrudes on purpose. The picker takes the CLOSEST intersection (see the
 * 2026-04-29 occlusion note), so a small target inside a larger one is only
 * reachable if its own surface is nearer the camera — geometry decides this, not
 * a priority field. Sitting the cube 125 mm above the slats is what makes the
 * end handle grabbable from every angle the seat is.
 */
const EDGE_HANDLE_SIZE_M = 0.13;
const EDGE_HANDLE_LIFT_M = 0.06;

export interface InteriorHandleDescriptor {
	/** Stable across rebuilds — the measurement target's id. Also the React key. */
	id: string;
	box: InteriorElementBox;
	/**
	 * Everything the mesh needs, assembled here rather than in the component.
	 *
	 * The measurement target already names what a drag on this handle moves
	 * (`planTarget`) and which pick layer it belongs to, so the component is a pure
	 * map and the opacity triple comes from `getWallOverlayOpacities` — the single
	 * owner every other overlay reads. Building it per render also re-created these
	 * objects on every unrelated `SceneCanvas` render.
	 */
	userData: {
		measurementTarget: MeasurementTarget;
		hoverFrame: true;
		cursor: "grab";
		baseOpacity: number;
		baseOpacityHover: number;
		baseOpacityLocked: number;
	};
}

function descriptor(
	target: MeasurementTarget,
	box: InteriorElementBox,
): InteriorHandleDescriptor {
	return {
		id: target.id,
		box,
		userData: {
			measurementTarget: target,
			// Marks the handle as eligible for the terracotta hover ring. Deliberately
			// NOT `interactionKind`: that field is `DragController`'s dispatch key and
			// enumerates the families it can drive (`opening` | `partition`), so
			// borrowing it for decoration would opt these handles into a dispatcher
			// that has no branch for them.
			hoverFrame: true,
			cursor: "grab",
			...getWallOverlayOpacities(target.kind),
		},
	};
}

function edgeHandleBox(
	run: BenchRun,
	edge: BenchRunEdge,
	rooms: readonly InteriorRoom[],
	frame: InteriorFrameM,
): InteriorElementBox {
	const room = roomForRun(run, rooms);
	const footprint = benchRunFootprint(run, room);
	const points = benchRunHandlePointsMm(run, footprint);
	const point = edge === "start" ? points.startHandle : points.endHandle;
	const seatTopY = benchVerticalExtentM(
		run,
		frame.wallBaseY,
		frame.wallHeightM,
	).maxY;
	return {
		centerM: [
			point.xMm / 1000,
			seatTopY + EDGE_HANDLE_LIFT_M,
			point.zMm / 1000,
		],
		sizeM: [EDGE_HANDLE_SIZE_M, EDGE_HANDLE_SIZE_M, EDGE_HANDLE_SIZE_M],
	};
}

/**
 * Every grabbable interior element for a layout: one body handle per bench run,
 * two end handles per run, and the aggregat.
 */
export function createInteriorHandles(
	model: InteriorEditModel,
	frame: InteriorFrameM,
): InteriorHandleDescriptor[] {
	const handles: InteriorHandleDescriptor[] = [];

	for (const run of model.layout.benchRuns) {
		handles.push(
			descriptor(
				createBenchMeasurementTarget(run),
				benchRunBoxM(run, model.rooms, frame),
			),
		);
		const box = benchRunBoxM(run, model.rooms, frame);
		const room = roomForRun(run, model.rooms);
		const target = {
			...createBenchMeasurementTarget(run),
			id: `bench-height:${run.id}`,
			planTarget: { kind: "bench-height" as const, runId: run.id },
			seatHeightMm: benchSeatTopMm(run, room.wallHeightMm),
		};
		// A tall grip at the room-facing midpoint, above the seat and separate
		// from the two end grips. Its narrower width reads as a vertical axis.
		const centerM: [number, number, number] = [...box.centerM];
		centerM[1] = frame.wallBaseY + target.seatHeightMm / 1000 + 0.18;
		const outwardAxis = run.wall === "back" || run.wall === "front" ? 2 : 0;
		const inwardSign = run.wall === "back" || run.wall === "left" ? 1 : -1;
		centerM[outwardAxis] += (inwardSign * box.sizeM[outwardAxis]) / 2;
		const heightHandle = descriptor(target, {
			centerM,
			sizeM: [0.18, 0.34, 0.18],
		});
		heightHandle.userData.baseOpacity = 0.75;
		heightHandle.userData.baseOpacityHover = 0.95;
		heightHandle.userData.baseOpacityLocked = 0.95;
		handles.push(heightHandle);
		for (const edge of ["start", "end"] as BenchRunEdge[]) {
			handles.push(
				descriptor(
					createBenchEdgeMeasurementTarget(run, edge),
					edgeHandleBox(run, edge, model.rooms, frame),
				),
			);
		}
	}

	const heaterBox = interiorElementBoxM(
		{ kind: "heater" },
		model.layout,
		model.rooms,
		model.context.heaterSize,
		frame,
	);
	if (heaterBox) {
		handles.push(
			descriptor(
				createHeaterMeasurementTarget(
					model.layout.heater,
					model.context.heaterSize,
				),
				heaterBox,
			),
		);
	}

	return handles;
}

export interface InteriorEditSetup {
	model: InteriorEditModel;
	frame: InteriorFrameM;
	handles: InteriorHandleDescriptor[];
}

/**
 * The whole interior-editing setup for a build input, or null when the design has
 * no layout-driven interior.
 *
 * Derived ONCE per build input and handed to both the handle meshes and the drag
 * controller: the handles the user grabs and the geometry the drag resolves
 * against are then the same objects, not two derivations that agree today.
 */
export function deriveInteriorEditSetup(
	input: BuildInput,
): InteriorEditSetup | null {
	const model = deriveInteriorEditModel(input);
	if (!model) return null;
	const frame = deriveInteriorFrame(input);
	if (!frame) return null;
	return { model, frame, handles: createInteriorHandles(model, frame) };
}
